import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuLayoutPanelLeft, LuPalette, LuPanelRightClose, LuPanelRightOpen, LuSmartphone } from './icons';
import { MMECTopBar } from './MMECTopBar';
import { MMECNavRail, NavCategory } from './MMECNavRail';
import { MMECCommandPalette, PaletteAction } from './MMECCommandPalette';
import { useDataSource } from '../../contexts/DataSourceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMMECTheme, MMECPalette } from '../../contexts/MMECThemeContext';
import { ProShellFilterRailProvider, useProShellFilterRailSetMount } from '../../contexts/ProShellFilterRailContext';
import StableDataFetchBar from '../StableDataFetchBar';
import { OPEN_PALETTE_EVENT } from './mmecEvents';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import { navLabel } from './navLabelsAr';
import './MMECShell.ds.css';

const NAV_RAIL_STORAGE_KEY = 'mmec_nav_rail_open';
const FILTER_RAIL_STORAGE_KEY = 'mmec_filter_rail_open';


function readFlag(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {
    /* ignore */
  }
  return fallback;
}

function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Subscribe to a media query; SSR-safe, updates on change. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Below this the nav rail starts collapsed; the person can still expand it. */
const NARROW_NAV_QUERY = '(max-width: 1100px)';
/** Below this the filter rail floats over the canvas instead of squeezing it. */
const OVERLAY_FILTERS_QUERY = '(max-width: 1280px)';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

interface MMECShellProps {
  categories: NavCategory[];
  activeId: string;
  onSelect: (id: string) => void;
  children: React.ReactNode;
}

/** Renders active tool filter portals into the right rail scroll region. */
function ProFilterRailMountHost() {
  const setMount = useProShellFilterRailSetMount();
  return <div id="mmec-pro-filter-rail-scroll" ref={setMount} className="mmec-filter-rail-scroll" />;
}

const PALETTE_NAMES: Record<MMECPalette, string> = {
  hg: 'HG — dark terminal',
  sheet: '15 — burgundy',
  neon: 'HK — neon',
  cb: 'CB — light',
};

const css = `
  .mmec-shell {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    width: 100vw;
    background: var(--base);
    color: var(--txt);
    font-family: var(--font-ui);
    overflow: hidden;
  }
  .mmec-shell-body {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    position: relative;
  }

  /* ── Navigation rail (left) ────────────────────────────────────────── */
  .mmec-nav-rail-outer {
    flex-shrink: 0;
    width: 216px;
    height: 100%;
    overflow: hidden;
    border-inline-end: 1px solid var(--hair);
    transition: width var(--dur-panel) var(--ease);
  }
  .mmec-nav-rail-outer.is-collapsed { width: 56px; }

  /* ── Canvas ────────────────────────────────────────────────────────── */
  .mmec-shell-canvas {
    flex: 1;
    min-width: 0;
    overflow: auto;
    background: var(--base);
    position: relative;
  }

  /* ── Filter rail (right) ───────────────────────────────────────────── */
  .mmec-filter-handle {
    flex-shrink: 0;
    width: 14px;
    align-self: stretch;
    border: none;
    border-inline-start: 1px solid var(--hair);
    background: var(--void);
    color: var(--muted-2);
    cursor: pointer;
    padding: 0;
    display: grid;
    place-items: center;
    transition: background var(--dur-micro) var(--ease), color var(--dur-micro) var(--ease);
    z-index: 5;
  }
  .mmec-filter-handle:hover { background: var(--elev); color: var(--txt); }
  .mmec-filter-handle:focus-visible { outline-offset: -2px; }

  .mmec-filter-rail-outer {
    flex-shrink: 0;
    width: 272px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: var(--void);
    transition: width var(--dur-panel) var(--ease);
  }
  .mmec-filter-rail-outer.is-closed { width: 0; }

  /* Overlay mode (narrow viewports): the rail floats over the canvas's right
     edge instead of taking width from it. */
  .mmec-filter-rail-outer.is-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-end: 14px; /* leaves the handle visible */
    z-index: 30;
    border-inline-start: 1px solid var(--border);
    box-shadow: var(--lift-3);
    background: var(--elev);
  }
  .mmec-filter-rail-outer.is-overlay.is-closed {
    box-shadow: none;
    border-inline-start-color: transparent;
  }
  .mmec-filter-handle.is-overlay-open {
    background: var(--elev);
    color: var(--acc);
  }
  .mmec-filter-rail-inner {
    width: 272px;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: opacity var(--dur-control) var(--ease);
  }
  .mmec-filter-rail-outer.is-closed .mmec-filter-rail-inner { opacity: 0; }
  .mmec-filter-rail-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 14px;
    border-bottom: 1px solid var(--hair);
  }
  .mmec-filter-rail-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 10px 20px;
    font-family: var(--font-ui);
  }
`;

const MMECShellInner: React.FC<MMECShellProps> = ({ categories, activeId, onSelect, children }) => {
  const { dataSource } = useDataSource();
  const { language } = useLanguage();
  const { setMode, setPalette } = useMMECTheme();
  const [navPref, setNavPref] = useState(() => !readFlag(NAV_RAIL_STORAGE_KEY, true));
  const [filterPref, setFilterPref] = useState(() => readFlag(FILTER_RAIL_STORAGE_KEY, true));

  // On a narrow viewport the rails yield to the canvas by default. A toggle
  // while narrow is remembered only for that narrow session, so widening the
  // window brings back the stored desktop preference untouched.
  const narrow = useMediaQuery(NARROW_NAV_QUERY);
  const overlayFilters = useMediaQuery(OVERLAY_FILTERS_QUERY);
  const [narrowNavOverride, setNarrowNavOverride] = useState<boolean | null>(null);
  const [overlayFilterOverride, setOverlayFilterOverride] = useState<boolean | null>(null);
  useEffect(() => setNarrowNavOverride(null), [narrow]);
  useEffect(() => setOverlayFilterOverride(null), [overlayFilters]);

  const navCollapsed = narrow ? (narrowNavOverride ?? true) : navPref;
  const filterRailOpen = overlayFilters ? (overlayFilterOverride ?? false) : filterPref;
  const [paletteOpen, setPaletteOpen] = useState(false);
  const canvasRef = useRef<HTMLElement>(null);

  useEffect(() => writeFlag(NAV_RAIL_STORAGE_KEY, !navPref), [navPref]);

  // Floating controls that live outside the shell (fetch log, launchers) read
  // this to keep clear of the rail. Cleared on unmount so Simple mode gets 0.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--mmec-rail-offset', navCollapsed ? '56px' : isDesignSystemOn() ? '200px' : '216px');
    return () => {
      root.style.removeProperty('--mmec-rail-offset');
    };
  }, [navCollapsed]);
  useEffect(() => writeFlag(FILTER_RAIL_STORAGE_KEY, filterPref), [filterPref]);

  const toggleNav = useCallback(() => {
    if (narrow) setNarrowNavOverride((o) => !(o ?? true));
    else setNavPref((c) => !c);
  }, [narrow]);
  const toggleFilterRail = useCallback(() => {
    if (overlayFilters) setOverlayFilterOverride((o) => !(o ?? false));
    else setFilterPref((o) => !o);
  }, [overlayFilters]);

  // Overlay filters close on Escape — they float over the work.
  useEffect(() => {
    if (!(overlayFilters && filterRailOpen)) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverlayFilterOverride(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayFilters, filterRailOpen]);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // ⌘K / Ctrl+K anywhere; the top bar's search button dispatches the same event.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    const onOpen = () => setPaletteOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  // View change: scroll to the top and let the new tool settle in. The scroll
  // container keeps its scrollTop across content swaps, so without the reset a
  // switch could land someone halfway down a tool they have never seen.
  const prevActive = useRef(activeId);
  useEffect(() => {
    if (prevActive.current === activeId) return;
    prevActive.current = activeId;
    const el = canvasRef.current;
    if (!el) return;
    el.scrollTop = 0;
    if (prefersReducedMotion()) return;
    el.animate(
      [
        { opacity: 0, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 380, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }, [activeId]);

  const isAr = language === 'ar';
  const filterLabel = filterRailOpen
    ? isAr ? 'إخفاء المرشحات' : 'Hide filters'
    : isAr ? 'إظهار المرشحات' : 'Show filters';

  const paletteActions = useMemo<PaletteAction[]>(() => {
    const layout = isAr ? 'تخطيط' : 'Layout';
    const theme = isAr ? 'سمة' : 'Theme';
    return [
      {
        id: 'toggle-nav',
        title: navCollapsed ? (isAr ? 'توسيع القائمة' : 'Expand navigation') : (isAr ? 'طي القائمة' : 'Collapse navigation'),
        hint: layout,
        icon: LuLayoutPanelLeft,
        run: toggleNav,
      },
      {
        id: 'toggle-filters',
        title: filterLabel,
        hint: layout,
        icon: filterRailOpen ? LuPanelRightClose : LuPanelRightOpen,
        run: toggleFilterRail,
      },
      {
        id: 'simple-mode',
        title: isAr ? 'التبديل إلى الوضع البسيط' : 'Switch to Simple mode',
        hint: layout,
        icon: LuSmartphone,
        run: () => setMode('simple'),
      },
      ...(Object.keys(PALETTE_NAMES) as MMECPalette[]).map((p) => ({
        id: `palette-${p}`,
        title: `${isAr ? 'اللوحة' : 'Palette'}: ${PALETTE_NAMES[p]}`,
        hint: theme,
        icon: LuPalette,
        run: () => setPalette(p),
      })),
    ];
  }, [isAr, navCollapsed, filterRailOpen, filterLabel, toggleNav, toggleFilterRail, setMode, setPalette]);

  const ds = isDesignSystemOn();
  // Fixed floaters outside the shell (Alerts bell, toast stack) mirror with the reading direction.
  useEffect(() => {
    if (!ds) return undefined;
    const root = document.documentElement;
    root.setAttribute('data-mmec-dir', isAr ? 'rtl' : 'ltr');
    return () => root.removeAttribute('data-mmec-dir');
  }, [ds, isAr]);
  const activeTitle = useMemo(() => {
    for (const c of categories) {
      const it = c.items.find((x) => x.id === activeId);
      if (it) return navLabel(it.title, language === 'ar');
    }
    return '';
  }, [categories, activeId, language]);

  if (ds) {
    // Design System layout (artboard web/shell): the rail runs the full height with the brand on top,
    // the top bar sits beside it and carries the active tool's title.
    return (
      <>
        <style>{css}</style>
        <div className="mmec-shell mmec-shell--ds" dir={isAr ? 'rtl' : 'ltr'}>
          <div className="mmec-shell-body">
          <div
            id="mmec-nav-rail-region"
            data-tour="mmec-nav-rail"
            className={`mmec-nav-rail-outer${navCollapsed ? ' is-collapsed' : ''}`}
          >
            <MMECNavRail
              categories={categories}
              activeId={activeId}
              onSelect={onSelect}
              collapsed={navCollapsed}
              onToggleCollapsed={toggleNav}
              onOpenSearch={openPalette}
              language={language}
            />
          </div>

            <div className="mmec-shell-col">
              <MMECTopBar dsTitle={activeTitle} />
              <div className="mmec-shell-work">
              <main className="mmec-shell-canvas" ref={canvasRef}>
                {dataSource === 'stable' && <StableDataFetchBar />}
                {children}
              </main>

              <button
                type="button"
                className={`mmec-filter-handle${overlayFilters && filterRailOpen ? ' is-overlay-open' : ''}`}
                onClick={toggleFilterRail}
                aria-expanded={filterRailOpen}
                aria-controls="mmec-filter-rail-region"
                title={filterLabel}
                aria-label={filterLabel}
              >
                {filterRailOpen ? (
                  <LuPanelRightClose size={12} strokeWidth={2} aria-hidden />
                ) : (
                  <LuPanelRightOpen size={12} strokeWidth={2} aria-hidden />
                )}
              </button>
              <div
                id="mmec-filter-rail-region"
                data-tour="mmec-filter-rail"
                className={`mmec-filter-rail-outer${filterRailOpen ? '' : ' is-closed'}${overlayFilters ? ' is-overlay' : ''}`}
                aria-hidden={!filterRailOpen}
              >
                <div className="mmec-filter-rail-inner">
                  <div className="mmec-filter-rail-header mmec-eyebrow">{isAr ? 'مرشحات الأداة' : 'Tool filters'}</div>
                  <ProFilterRailMountHost />
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      <MMECCommandPalette
        open={paletteOpen}
        onClose={closePalette}
        categories={categories}
        activeId={activeId}
        onSelect={onSelect}
        actions={paletteActions}
        language={language}
      />
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="mmec-shell">
        <MMECTopBar />
        <div className="mmec-shell-body">
          <div
            id="mmec-nav-rail-region"
            data-tour="mmec-nav-rail"
            className={`mmec-nav-rail-outer${navCollapsed ? ' is-collapsed' : ''}`}
          >
            <MMECNavRail
              categories={categories}
              activeId={activeId}
              onSelect={onSelect}
              collapsed={navCollapsed}
              onToggleCollapsed={toggleNav}
              onOpenSearch={openPalette}
              language={language}
            />
          </div>

          <main className="mmec-shell-canvas" ref={canvasRef}>
            {dataSource === 'stable' && <StableDataFetchBar />}
            {children}
          </main>

          <button
            type="button"
            className={`mmec-filter-handle${overlayFilters && filterRailOpen ? ' is-overlay-open' : ''}`}
            onClick={toggleFilterRail}
            aria-expanded={filterRailOpen}
            aria-controls="mmec-filter-rail-region"
            title={filterLabel}
            aria-label={filterLabel}
          >
            {filterRailOpen ? (
              <LuPanelRightClose size={12} strokeWidth={2} aria-hidden />
            ) : (
              <LuPanelRightOpen size={12} strokeWidth={2} aria-hidden />
            )}
          </button>
          <div
            id="mmec-filter-rail-region"
            data-tour="mmec-filter-rail"
            className={`mmec-filter-rail-outer${filterRailOpen ? '' : ' is-closed'}${overlayFilters ? ' is-overlay' : ''}`}
            aria-hidden={!filterRailOpen}
          >
            <div className="mmec-filter-rail-inner">
              <div className="mmec-filter-rail-header mmec-eyebrow">{isAr ? 'مرشحات الأداة' : 'Tool filters'}</div>
              <ProFilterRailMountHost />
            </div>
          </div>
        </div>
      </div>

      <MMECCommandPalette
        open={paletteOpen}
        onClose={closePalette}
        categories={categories}
        activeId={activeId}
        onSelect={onSelect}
        actions={paletteActions}
        language={language}
      />
    </>
  );
};

export const MMECShell: React.FC<MMECShellProps> = (props) => (
  <ProShellFilterRailProvider>
    <MMECShellInner {...props} />
  </ProShellFilterRailProvider>
);

export default MMECShell;
